import { useEffect, useState, useCallback } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { MagnifyingGlass, Receipt, Plus, X, WarningCircle, Clock, FunnelSimple, Lightning, Copy } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate, dmy } from "@/lib/format";
import { SourceBadge } from "@/components/SourceBadge";
import { Card, Loading, EmptyState } from "@/components/riq";
import { MerchantLogo } from "@/components/riq";

export function ReviewBadge({ status, code }) {
  if (code === "duplicate") return <span data-testid="review-badge-duplicate" className="rounded-full bg-[#FFF0D6] text-[#8a5a00] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide inline-flex items-center gap-1"><Copy size={11} weight="fill" /> Duplicate</span>;
  if (status === "rejected") return <span data-testid="review-badge-rejected" className="rounded-full bg-[#FFE3E1] text-[#b3261e] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide inline-flex items-center gap-1"><WarningCircle size={11} weight="fill" /> Rejected</span>;
  if (status === "needs_date_fix") return <span data-testid="review-badge-needs-fix" className="rounded-full bg-riq-gold/25 text-[#8a5a00] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide inline-flex items-center gap-1"><Clock size={11} weight="fill" /> Fix date</span>;
  if (status === "needs_review") return <span data-testid="review-badge-needs-review" className="rounded-full bg-riq-sage text-riq-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide inline-flex items-center gap-1"><WarningCircle size={11} weight="fill" /> Check total</span>;
  return null;
}

export default function Receipts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openAddReceipt } = useOutletContext();
  const cur = user?.currency || "ZAR";
  const [receipts, setReceipts] = useState(null);
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const from = params.get("from");
  const to = params.get("to");
  const rangeLabel = params.get("label");
  const dateFiltered = Boolean(from || to);

  const load = useCallback((query) => {
    let url;
    if (query) {
      url = `/receipts?q=${encodeURIComponent(query)}`;
    } else if (from || to) {
      const qs = new URLSearchParams({ limit: "200" });
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      url = `/receipts?${qs.toString()}`;
    } else {
      url = "/receipts?limit=100";
    }
    api.get(url).then((r) => setReceipts(r.data.receipts)).catch(() => setReceipts([]));
  }, [from, to]);

  useEffect(() => {
    const t = setTimeout(() => load(q), 350);
    return () => clearTimeout(t);
  }, [q, load]);

  const clearDateFilter = () => {
    setParams({}, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary">Receipts</h1>
        <button data-testid="receipts-add-btn" onClick={openAddReceipt} className="flex items-center gap-2 rounded-full bg-riq-primary text-white px-5 py-2.5 font-medium hover:-translate-y-0.5 transition-transform"><Plus size={18} weight="bold" /> Add</button>
      </div>

      <div className="relative">
        <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input data-testid="receipt-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search merchant, item, category or 'over R500'…"
          className="w-full bg-white rounded-full border border-riq-border pl-12 pr-10 py-3.5 text-sm outline-none focus:border-riq-primary" />
        {q && <button onClick={() => setQ("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400"><X size={18} /></button>}
      </div>

      {dateFiltered && !q && (
        <div data-testid="receipts-date-filter" className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-riq-primary/10 text-riq-primary px-4 py-2 text-sm font-medium">
            <FunnelSimple size={16} weight="bold" />
            {rangeLabel || `${dmy(from)} – ${dmy(to)}`}
            <button data-testid="receipts-clear-date-filter" onClick={clearDateFilter} className="ml-1 rounded-full hover:bg-riq-primary/15 p-0.5"><X size={15} /></button>
          </span>
        </div>
      )}

      {receipts === null ? <Loading /> : receipts.length === 0 ? (
        <EmptyState icon={Receipt} title={q ? "No matching receipts" : dateFiltered ? "No receipts in this period" : "No receipts yet"} subtitle={q ? "Try a different search term." : dateFiltered ? "There are no receipts dated in this range." : "Add your first receipt to build your spending history."} />
      ) : (
        <Card className="divide-y divide-riq-border p-0">
          {receipts.map((r) => (
            <button key={r.id} data-testid={`receipt-item-${r.id}`} onClick={() => navigate(`/receipts/${r.id}`)} className="w-full flex items-center gap-4 px-5 py-4 hover:bg-secondary/50 transition-colors text-left">
              <MerchantLogo name={r.merchant_name} size={44} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate flex items-center gap-2"><span className="truncate">{r.merchant_name}</span><ReviewBadge status={r.review_status} code={r.review_code} /><SourceBadge source={r.source} iconOnly />{(r.ocr_engine === "gemini" || r.ocr_engine === "emergent" || r.ocr_engine === "tesseract") && (<span data-testid={`ocr-fallback-${r.id}`} title={r.ocr_engine === "gemini" ? "Scanned via Gemini backup — primary scanner was unavailable" : r.ocr_engine === "emergent" ? "Scanned via Emergent backup — primary scanner was unavailable" : "Scanned via free OCR backup — primary scanner was unavailable"} className="shrink-0 inline-flex items-center rounded-full bg-riq-gold/20 text-riq-gold p-0.5"><Lightning size={12} weight="fill" /></span>)}</div>
                <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-1.5">
                  <span>Receipt <span className="font-medium text-neutral-600">{dmy(r.date)}</span></span>
                  {r.created_at && <span>· Scanned {shortDate(r.created_at)}</span>}
                  <span>· {r.merchant_category}</span>
                  {r.matched_item ? <span>· matched: {r.matched_item}</span> : null}
                </div>
              </div>
              <div className={`font-head font-semibold ${r.review_status === "rejected" ? "line-through text-neutral-400" : ""}`}>{money(r.total, cur)}</div>
            </button>
          ))}
        </Card>
      )}
    </div>
  );
}
